/**
 * Legal acceptance service — append-only records of what a user accepted.
 *
 * Class-based singleton per architecture rules. Every method records an
 * acceptance into `legal_acceptances`, which is append-only and versioned
 * (immutable UPDATE/DELETE triggers in the security-hardening migration),
 * so a row is a person, a wording, and a moment.
 *
 * Idempotent by re-read rather than upsert: accepting the same version twice
 * is a second signature of the same text, and overwriting the first would
 * destroy the earlier timestamp — the one thing the record exists to
 * preserve.
 */
import { supabase } from '../supabase'
import { ServiceError } from '../errors/service-error'
import { NCND_DOCUMENT_TYPE, NCND_DOCUMENT_URL, NCND_DOCUMENT_VERSION } from '../constants/ncnd'

export type RentalTermsVersionInfo = {
  version: string
  termsUrl: string
  cancellationUrl: string
  summaryEs: string
  summaryEn: string
  effectiveAt: string | null
  accepted: boolean
}
export type CurrentRentalTerms = RentalTermsVersionInfo & {
  upcoming: RentalTermsVersionInfo | null
}

function readRentalTermsVersion(value: unknown): RentalTermsVersionInfo {
  if (!value || typeof value !== 'object') throw new Error('Rental terms unavailable')
  const item = value as Record<string, unknown>
  if (
    typeof item.version !== 'string' ||
    !item.version ||
    typeof item.terms_url !== 'string' ||
    !item.terms_url.startsWith('https://tago.rent/') ||
    typeof item.cancellation_url !== 'string' ||
    !item.cancellation_url.startsWith('https://tago.rent/') ||
    typeof item.summary_es !== 'string' ||
    typeof item.summary_en !== 'string'
  ) {
    throw new Error('Current rental terms response is invalid')
  }
  return {
    version: item.version,
    termsUrl: item.terms_url,
    cancellationUrl: item.cancellation_url,
    summaryEs: item.summary_es,
    summaryEn: item.summary_en,
    effectiveAt: typeof item.effective_at === 'string' ? item.effective_at : null,
    accepted: item.accepted === true,
  }
}

function readCurrentRentalTerms(value: unknown): CurrentRentalTerms {
  const current = readRentalTermsVersion(value)
  const item = value as Record<string, unknown>
  return {
    ...current,
    upcoming: item.upcoming == null ? null : readRentalTermsVersion(item.upcoming),
  }
}

class LegalServiceImpl {
  async currentRentalTerms(): Promise<CurrentRentalTerms> {
    const { data, error } = await supabase.rpc('current_rental_terms')
    if (error) throw ServiceError.fromSupabaseError(error)
    return readCurrentRentalTerms(data)
  }

  async acceptCurrentRentalTerms(expectedVersion: string): Promise<void> {
    const { data, error } = await supabase.rpc('accept_current_rental_terms', {
      p_expected_version: expectedVersion,
    })
    if (error) throw ServiceError.fromSupabaseError(error)
    if (
      !data ||
      typeof data !== 'object' ||
      (data as Record<string, unknown>).version !== expectedVersion
    ) {
      throw new Error('Rental terms acceptance could not be verified')
    }
  }

  async acceptNoticedRentalTerms(expectedVersion: string): Promise<void> {
    const { data, error } = await supabase.rpc('accept_noticed_rental_terms', {
      p_expected_version: expectedVersion,
    })
    if (error) throw ServiceError.fromSupabaseError(error)
    if (
      !data ||
      typeof data !== 'object' ||
      (data as Record<string, unknown>).version !== expectedVersion
    ) {
      throw new Error('Upcoming rental terms acceptance could not be verified')
    }
  }

  /**
   * Record acceptance of the NCND clause (Terms of Service v1.3, section 10).
   *
   * Called when a guest confirms a booking: accepting the terms checkbox is
   * already a precondition of payment, so the acceptance row is written the
   * moment the booking is created. The record carries no PII beyond the
   * profile id; `ip_address` and `user_agent` are populated server-side by
   * Postgres defaults when present.
   */
  async acceptTermsNcnd(userId: string): Promise<void> {
    const { data: existing, error: readError } = await supabase
      .from('legal_acceptances')
      .select('id')
      .eq('profile_id', userId)
      .eq('document_type', NCND_DOCUMENT_TYPE)
      .eq('document_version', NCND_DOCUMENT_VERSION)
      .limit(1)
    if (readError) throw ServiceError.fromSupabaseError(readError)
    if ((existing ?? []).length > 0) return

    const { error } = await supabase.from('legal_acceptances').insert({
      profile_id: userId,
      document_type: NCND_DOCUMENT_TYPE,
      document_version: NCND_DOCUMENT_VERSION,
      document_url: NCND_DOCUMENT_URL,
    })
    if (error) throw ServiceError.fromSupabaseError(error)
  }
}

export const legalService = new LegalServiceImpl()
